if (!customElements.get('paginated-grid')) {
  class GridPagination extends HTMLElement {
    constructor() {
      super();

      this.init();
    }

    init() {
      this.type = this.getAttribute('type') || 'product';

      const {
        grid = `#${this.type}-grid`,
        pagination = `#${this.type}-pagination`,
        offset = 2000,
        automatic = true,
        cardSelector = `.card--${this.type}`,
        identifier = `${this.type}Id`,
      } = this.dataset;

      this.cardSelector = cardSelector;
      this.identifier = identifier;
      this.settings = { grid, pagination, offset };
      this.grid = this.querySelector(grid);
      this.pagination = this.querySelector(pagination);
      this.automatic = automatic !== 'false';
      this.cardPageTriggers = new Map();

      this.observer = new IntersectionObserver(this.observe.bind(this), {
        rootMargin: `0px 0px ${offset}px 0px`,
      });
      this.pageTriggerObserver = new IntersectionObserver(this.observePageTriggers.bind(this));

      this.handleClick = this.onClick.bind(this);

      this.setupAriaLiveRegion();

      this.updatePagination();

      if (this.page > 1) {
        this.setPageAnchor(this.grid.querySelector(this.cardSelector), this.page);
        this.loadPreviousPages();
      }
    }

    setupAriaLiveRegion() {
      const existingRegion = this.querySelector('[data-pagination-announcement]');
      if (existingRegion) {
        this.ariaLiveRegion = existingRegion;
      }
    }

    static get observedAttributes() {
      return ['page'];
    }

    attributeChangedCallback(name, previousValue, value) {
      if (name === 'page' && Number(value) !== this.page) {
        this.page = Number(value);
      }
    }

    set page(value) {
      this._page = value;
      this.setAttribute('page', this._page);
      const url = new URL(location);
      if (this._page !== 1) {
        url.searchParams.set('page', this._page);
      } else {
        url.searchParams.delete('page');
      }
      history.pushState({}, '', url);
    }

    get page() {
      return this._page ?? this.getAttribute('page') ?? 1;
    }

    observe(entries, observer) {
      const entry = entries[0];

      if (entry.isIntersecting) {
        this.loadMore(this.automatic);

        observer.disconnect();
      }
    }

    // Update current page based on scroll direction and trigger visibility
    observePageTriggers(entries, observer) {
      const entry = entries[0];

      if (entry.isIntersecting && window.scrollDirection === 'down') {
        console.debug('Page trigger observed, incrementing page', entry.target);
        this.setPageFromTrigger(entry.target);
      } else if (!entry.isIntersecting && window.scrollDirection === 'up') {
        console.debug('Page trigger observed leaving, decrementing page', entry.target);
        this.setPageFromTrigger(entry.target, -1);
      }
    }

    setPageFromTrigger(trigger, modifier = 0) {
      const page = this.cardPageTriggers.get(trigger) + modifier;
      this.page = Math.max(1, page);
    }

    async loadPreviousPages() {
      // Store first item for scroll position lock
      const firstItem = this.grid.querySelector(this.cardSelector);
      const stickyHeader = document.querySelector('sticky-header');

      // Get previous page numbers in reverse order
      const previousPages = range(1, this.page - 1).toReversed();

      // Loop through previous pages and prepend to grid
      for (const page of previousPages) {
        const url = new URL(location);
        const publishEvent = false;
        url.searchParams.set('page', page);
        await this.loadMore(false, { url, publishEvent });
        this.updateItems(page, { prepend: true });

        // Prevent header reveal ensure scroll position is at first item of the current page
        stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
        firstItem.scrollIntoView({ behavior: 'instant' });
      }
    }

    async loadMore(update = false, {
      url = this.paginationLink?.dataset?.href || this.paginationLink?.href,
      publishEvent = true,
    } = {}) {
      if (this.isLoading) return;

      if (!url) return;

      this.isLoading = true;

      let response = await fetch(url);

      const newPageMarkup = await response.text();
      const parser = new DOMParser();
      this.newPage = parser.parseFromString(newPageMarkup, 'text/html');

      this.isLoading = false;

      if (publishEvent) {
        publish(PUB_SUB_EVENTS.gridContentFetch, {
          type: this.type,
          // TODO: Test this value
          page: this.page + 1
        });
      }

      if (update) {
        this.update();
      }
    }

    // TODO: Consider waiting for image preload if update is passed to loadMore (otherwise remove this method)
    preloadImages() {
      if (!this.newPage) return;

      this.newPage.querySelector(this.settings.grid).querySelectorAll('.card img').forEach(img => {
        const image = new Image();
        image.src = img.src;
        image.srcset = img.srcset;
      });
    }

    update() {
      if (this.isLoading) {
        if (!this.loadMoreUnsubscriber) {
          this.paginationLink.classList.add('loading');
          console.debug('Grid Pagination update deferred: Currently fetching new page content.');
          this.loadMoreUnsubscriber = subscribe(PUB_SUB_EVENTS.gridContentFetch, ({ type, page }) => {
            if (type === this.type && page === this.page + 1) {
              this.update();
              this.loadMoreUnsubscriber();
              delete this.loadMoreUnsubscriber;
            }
          });

          return;
        }

        console.debug('Grid Pagination update aborted: Currently fetching new page content.');

        return;
      }

      this.paginationLink.classList.add('loading');

      if (!this.newPage) {
        console.debug(
          'Grid Pagination update triggering fetch: New page content has not been loaded.'
        );
        this.loadMore(true);
        return;
      }

      const paginationUrl = this.paginationLink.dataset?.href || this.paginationLink.href;

      // Get page number from pagination URL
      const pageMatch = paginationUrl.match(/[?&]page=(\d+)/);
      // Update page number via URL match or increment
      const page = Number(pageMatch?.[1] ?? this.page + 1);

      const newPagination = this.newPage.querySelector(this.settings.pagination);
      const { ids: newItemIds, elements: newItemElements } = this.updateItems(page);

      if (typeof newPagination === 'undefined') {
        this.end();
      } else {
        this.updatePagination(newPagination);
      }

      this.updatePage(page, newItemIds);

      this.handleAccessibility(newItemElements);

      this.newPage = null;
    }

    updateItems(page, { prepend = false } = {}) {
      const newContainer = this.newPage.querySelector(this.settings.grid);
      const newItemIds = Array.from(newContainer.querySelectorAll(this.cardSelector)).map(
        el => el.dataset[this.identifier]
      );
      const newItemElements = this.updateHTML(page, newContainer.innerHTML, newItemIds, { prepend });

      return { ids: newItemIds, elements: newItemElements };
    }

    updateHTML(page, html, items, { prepend = false } = {}) {
      const position = prepend ? 'afterbegin' : 'beforeend';
      this.grid.insertAdjacentHTML(position, html);
      const newItems = []

      this.grid.querySelectorAll(this.cardSelector).forEach(card => {
        if (items.includes(card.dataset[this.identifier])) {
          // reinjectScripts(card);
          newItems.push(card);
        }
      });

      this.setPageAnchor(items[0], page);

      // Animate new items if not prepending
      if (!prepend) {
        gsap.fromTo(newItems, {
          autoAlpha: 0,
          scale: 0.9,
        }, {
          autoAlpha: 1,
          scale: 1,
          duration: 1,
          ease: 'power2.inOut',
          stagger: {
            each: 0.1,
            from: 'start',
            grid: 'auto',
          },
        });
      }

      return newItems;
    }

    setPageAnchor(cardId, page) {
      console.log('Setting page anchor', `data-${camelToKebab(this.identifier)}`, cardId, page);
      const card = cardId instanceof HTMLElement ? cardId : this.grid.querySelector(`[data-${camelToKebab(this.identifier)}="${cardId}"]`);
      console.log('Setting page anchor', card, page);
      this.cardPageTriggers.set(card, Number(page));
      this.pageTriggerObserver.observe(card);
    }

    updatePage(page, items) {
      this.page = page;

      // Publish grid content update event
      publish(PUB_SUB_EVENTS.gridContentUpdate, {
        data: {
          type: this.type,
          page: this.page,
          items,
        },
      });
    }

    handleAccessibility(newItemElements) {
      if (!newItemElements || newItemElements.length === 0) return;

      const itemCount = newItemElements.length;
      const itemType = this.type === 'product' ? 'product' : 'item';
      const itemTypePlural = itemCount === 1 ? itemType : `${itemType}s`;
      
      console.log('Handling accessibility', this.ariaLiveRegion);

      if (this.ariaLiveRegion) {
        this.ariaLiveRegion.textContent = `${itemCount} more ${itemTypePlural} loaded.`;
      }

      requestAnimationFrame(() => {
        const firstNewItem = newItemElements[0];
        if (firstNewItem) {
          firstNewItem.setAttribute('tabindex', '-1');
          firstNewItem.focus({ preventScroll: false });
          
          firstNewItem.addEventListener('blur', () => {
            firstNewItem.removeAttribute('tabindex');
          }, { once: true });
        }
      });
    }

    updatePagination(newPagination) {
      if (newPagination) {
        this.pagination.innerHTML = newPagination.innerHTML;
      }

      this.paginationLink = this.pagination.querySelector('[data-pagination-link]');
      this.scrollTrigger = this.pagination.querySelector('[data-scroll-trigger]');
      initAnchor(this.scrollTrigger);

      this.observer.observe(this.pagination);
      this.paginationLink?.addEventListener('click', this.handleClick);
    }

    resetPagination(newPagination) {
      this.updatePagination(newPagination);

      // Disconnect active observers
      this.destroy();

      // Reset page number
      this.setAttribute('page', 1);

      // Reset scroll position
      window.scrollTo(0, 0);

      // Re-initialize grid pagination
      this.init();
    }

    onClick(e) {
      e.preventDefault();

      this.update();
    }

    end() {
      // TODO: Check if removal is necessary
      this.pagination.remove();
      this.observer.disconnect();
    }

    destroy() {
      // Disconnect active observers
      this.observer.disconnect();
      this.pageTriggerObserver.disconnect();

      // Clear any cached page content
      this.newPage = null;

      // Clear card triggers
      this.cardPageTriggers.clear();
    }
  }
  customElements.define('paginated-grid', GridPagination);
}